import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const WeatherInfo = ({ weather }) => {
  const [showForecast, setShowForecast] = useState(false);
  const [forecast, setForecast] = useState([]);

  useEffect(() => {
    // Mock forecast data
    const mockForecast = [
      {
        time: '14:00',
        temperature: weather?.temperature || 24,
        condition: 'Ensolarado',
        icon: 'Sun',
        rainChance: 10
      },
      {
        time: '15:00',
        temperature: weather?.temperature + 1 || 25,
        condition: 'Parcialmente nublado',
        icon: 'Cloud',
        rainChance: 15
      },
      {
        time: '16:00',
        temperature: weather?.temperature - 1 || 23,
        condition: 'Nublado',
        icon: 'Cloud',
        rainChance: 25
      },
      {
        time: '17:00',
        temperature: weather?.temperature - 2 || 22,
        condition: 'Possível chuva',
        icon: 'CloudRain',
        rainChance: 40
      }
    ];
    setForecast(mockForecast);
  }, [weather]);

  if (!weather) return null;

  const getWeatherIcon = (iconName) => {
    const iconMap = {
      'Sun': 'Sun',
      'Cloud': 'Cloud',
      'CloudRain': 'CloudRain',
      'Zap': 'Zap'
    };
    return iconMap?.[iconName] || 'Sun';
  };

  const getWeatherColor = (condition, rainChance) => {
    if (rainChance > 60) return 'text-error';
    if (rainChance > 30) return 'text-warning';
    if (condition?.toLowerCase()?.includes('sol')) return 'text-yellow-500';
    return 'text-info';
  };

  const getRecommendation = () => {
    if (weather?.rainChance > 60) {
      return {
        icon: 'Umbrella',
        text: 'Alta chance de chuva - considere reagendar',
        color: 'text-error bg-error/10 border-error/20'
      };
    }
    if (weather?.rainChance > 30) {
      return {
        icon: 'AlertTriangle',
        text: 'Possível chuva - monitore a previsão',
        color: 'text-warning bg-warning/10 border-warning/20'
      };
    }
    if (weather?.temperature > 30) {
      return {
        icon: 'Thermometer',
        text: 'Muito calor - hidrate-se bem',
        color: 'text-orange-600 bg-orange-50 border-orange-200'
      };
    }
    return {
      icon: 'CheckCircle',
      text: weather?.forecast || 'Clima ideal para futebol',
      color: 'text-success bg-success/10 border-success/20'
    };
  };

  const recommendation = getRecommendation();

  return (
    <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-lg shadow-elevation-2 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center">
            <Icon 
              name={getWeatherIcon(weather?.icon)} 
              size={20} 
              className="text-white" 
            />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-foreground">
              Condições do Tempo
            </h3>
            <p className="text-sm text-muted-foreground">
              Previsão para o horário da pelada
            </p>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          iconName={showForecast ? 'ChevronUp' : 'ChevronDown'}
          onClick={() => setShowForecast(!showForecast)}
        />
      </div>
      {/* Current Weather */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {/* Temperature */}
        <div className="bg-white/80 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-sky-600 mb-1">
            {weather?.temperature}°C
          </div>
          <div className="text-sm text-muted-foreground">
            Temperatura
          </div>
        </div>

        {/* Rain Chance */}
        <div className="bg-white/80 rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold mb-1 ${
            weather?.rainChance > 50 ? 'text-error' : 
            weather?.rainChance > 25 ? 'text-warning' : 'text-success'
          }`}>
            {weather?.rainChance}%
          </div>
          <div className="text-sm text-muted-foreground">
            Chance de chuva
          </div>
        </div>

        {/* Wind Speed */}
        <div className="bg-white/80 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-sky-600 mb-1">
            {weather?.windSpeed} km/h
          </div>
          <div className="text-sm text-muted-foreground">
            Vento
          </div>
        </div>
      </div>
      {/* Weather Condition */}
      <div className="flex items-center justify-center space-x-2 mb-4 p-3 bg-white/60 rounded-lg">
        <Icon 
          name={getWeatherIcon(weather?.icon)} 
          size={18} 
          className={getWeatherColor(weather?.condition, weather?.rainChance)}
        />
        <span className="text-sm font-medium text-foreground">
          {weather?.condition}
        </span>
        {weather?.humidity && (
          <span className="text-sm text-muted-foreground ml-2">
            • {weather?.humidity}% umidade
          </span>
        )}
      </div>
      {/* Weather Recommendation */}
      <div className={`p-3 border rounded-lg ${recommendation?.color}`}>
        <div className="flex items-start space-x-2">
          <Icon name={recommendation?.icon} size={16} className="mt-0.5" />
          <div>
            <p className="text-sm font-medium">
              {recommendation?.text}
            </p>
          </div>
        </div>
      </div>
      {/* Hourly Forecast */}
      {showForecast && (
        <div className="mt-4 pt-4 border-t border-sky-200">
          <h4 className="font-medium text-foreground mb-3 flex items-center">
            <Icon name="Clock" size={16} className="mr-2 text-sky-500" />
            Previsão por Hora
          </h4>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {forecast?.map((hour, index) => (
              <div 
                key={index} 
                className="bg-white/60 rounded-lg p-3 text-center"
              >
                <div className="text-sm font-medium text-foreground mb-2">
                  {hour?.time}
                </div>
                
                <div className="flex items-center justify-center mb-2">
                  <Icon 
                    name={getWeatherIcon(hour?.icon)} 
                    size={20} 
                    className={getWeatherColor(hour?.condition, hour?.rainChance)}
                  />
                </div>
                
                <div className="text-lg font-bold text-sky-600 mb-1">
                  {hour?.temperature}°
                </div>
                
                <div className="text-xs text-muted-foreground mb-1">
                  {hour?.condition}
                </div>
                
                <div className={`text-xs font-medium ${
                  hour?.rainChance > 50 ? 'text-error' : 
                  hour?.rainChance > 25 ? 'text-warning' : 'text-success'
                }`}>
                  {hour?.rainChance}% chuva
                </div>
              </div>
            ))}
          </div>

          {/* Weather Actions */}
          <div className="mt-4 flex space-x-3">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              className="flex-1 border-sky-300 text-sky-700 hover:bg-sky-50"
              onClick={() => {
                // Refresh weather data
                alert('Previsão atualizada!');
              }}
            >
              Atualizar
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Bell"
              iconPosition="left"
              className="flex-1 border-sky-300 text-sky-700 hover:bg-sky-50"
              onClick={() => {
                // Set weather alert
                alert('Alerta de clima ativado!');
              }}
            >
              Alertas
            </Button>
          </div>

          {/* Weather Tips */}
          <div className="mt-4 p-3 bg-sky-50/80 border border-sky-200/50 rounded-lg">
            <h5 className="text-sm font-medium text-foreground mb-2 flex items-center">
              <Icon name="Lightbulb" size={14} className="mr-2 text-sky-500" />
              Dicas para o Jogo
            </h5>
            
            <ul className="space-y-1 text-xs text-muted-foreground">
              {weather?.rainChance > 30 && (
                <li className="flex items-start space-x-2">
                  <Icon name="Umbrella" size={12} className="mt-0.5 text-sky-500" />
                  <span>Leve uma chuva ou guarda-chuva</span>
                </li>
              )}
              
              {weather?.temperature > 25 && (
                <li className="flex items-start space-x-2">
                  <Icon name="Droplets" size={12} className="mt-0.5 text-sky-500" />
                  <span>Hidrate-se bem antes, durante e após o jogo</span>
                </li>
              )}
              
              {weather?.windSpeed > 15 && (
                <li className="flex items-start space-x-2">
                  <Icon name="Wind" size={12} className="mt-0.5 text-sky-500" />
                  <span>Vento forte pode afetar passes e chutes</span>
                </li>
              )}
              
              <li className="flex items-start space-x-2">
                <Icon name="Shirt" size={12} className="mt-0.5 text-sky-500" />
                <span>
                  {weather?.temperature > 25 
                    ? 'Use roupas leves e protetor solar'
                    : weather?.temperature < 18
                    ? 'Vista-se em camadas para se aquecer' :'Temperatura ideal para atividades físicas'
                  }
                </span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherInfo;